/**
 * Shared recommendation types.
 *
 * Rank is configuration, not architecture: weights affect ORDER ONLY.
 * Membership is decided by the caller's retrieval / visibility layer.
 */

/** Classes of signal the ranker may consider. */
export type SignalClass =
  /** How well a record matches requested parameters (keyword, city, brief). */
  | 'fit'
  /** What was actually sold, and alongside what. */
  | 'evidence'
  /** Explicit human judgement about quality or suitability. */
  | 'curation'
  /** Deliberate commercial promotion (boost). */
  | 'commercial'
  /** Rating, completeness, freshness. */
  | 'quality';

export const SIGNAL_CLASSES: readonly SignalClass[] = [
  'fit',
  'evidence',
  'curation',
  'commercial',
  'quality',
] as const;

/**
 * One organisation's active weight set. Versioned externally; this package
 * never imports live config — callers pass it in.
 */
export type WeightSet = {
  readonly id: string;
  readonly weights: Readonly<Partial<Record<SignalClass, number>>>;
};

/** Neutral set: every class unweighted → deterministic tie-break only. */
export const NEUTRAL_WEIGHTS: WeightSet = { id: 'neutral', weights: {} };

/**
 * Default sold-history builder weights (mini-planner / itinerary-builder).
 * Mapped onto SignalClass buckets for the shared ranker.
 */
export const DEFAULT_WEIGHTS: WeightSet = {
  id: 'builder-default',
  weights: {
    evidence: 0.75, // popularity 0.45 + coOccurrence 0.3
    fit: 0.25, // keyword 0.2 + city 0.05
  },
};

/**
 * Fine-grained sold-corpus weights used by `scoreSoldProductParts`.
 * Kept for parity with the itinerary-builder formula.
 */
export const BUILDER_WEIGHTS = {
  /** log(1 + effective sold trips) — primary signal. */
  popularity: 0.45,
  /** Co-occurrence with keyword/popular seeds (lift-aware). */
  coOccurrence: 0.3,
  /** Keyword / allowlist title hits — leads when keywords present. */
  keyword: 0.2,
  /** Requested city / metro hint match. */
  city: 0.05,
} as const;

/** Same keys as `BUILDER_WEIGHTS`, any number — weights are configuration. */
export type BuilderWeights = {
  readonly [K in keyof typeof BUILDER_WEIGHTS]: number;
};

/**
 * Per-component score for harness evidence ("why this item").
 * `total` is popularity + keyword + city (+ coOccurrence when applied).
 */
export type ScoreParts = {
  popularity: number;
  keyword: number;
  city: number;
  coOccurrence: number;
  total: number;
};

/** Minimal product row the pure ranker needs. Callers may carry extra fields. */
export type RecommendCandidate = {
  readonly id: string;
  readonly title?: string;
  readonly subcategory?: string | null;
  readonly city?: string | null;
  readonly country?: string | null;
  readonly category?: string | null;
  /** Sold trips in the conditioned slice (org/country/month…). */
  readonly soldTrips?: number;
  readonly cancelledTrips?: number;
  readonly keywordHits?: number;
  readonly cityHit?: boolean;
  /** Co-trips with a seed (e.g. chosen hotel). */
  readonly coTrips?: number;
  readonly coLift?: number | null;
  readonly distinctAccounts?: number | null;
  /**
   * Optional precomputed total. When set, `recommendProducts` uses it and
   * does not re-score sold/keyword inputs.
   */
  readonly score?: number;
};

export type ScoredCandidate<T extends RecommendCandidate = RecommendCandidate> =
  T & {
    readonly score: number;
    readonly scoreParts: ScoreParts;
  };

export type RecommendProductsOptions = {
  readonly keywordsActive?: boolean;
  readonly baselineCancelRate?: number;
  readonly weights?: BuilderWeights;
  /** Max rows to return. Default: all (order-only full permutation). */
  readonly limit?: number;
};
